/**
 * /admin/encryption — column-level at-rest encryption status + rekey
 * sweep trigger (OCP-70).
 *
 * Shows: active key fingerprint, previous-key fingerprints (rotation
 * history), last sweep summary. Operator can trigger a sweep —
 * usually after rotating `OCP_TOKEN_ENCRYPTION_KEY` in Coolify env,
 * to re-encrypt every `_enc` column under the new key so the previous
 * key can be safely dropped from `_KEYS_PREVIOUS`.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface Status {
  active_key_fingerprint: string;
  previous_key_fingerprints: string[];
  previous_key_count: number;
  /** OCP-721 - values still encrypted under a previous key; a previous key may be dropped only at 0. */
  values_under_previous_keys?: number;
  columns?: Array<{ table: string; column: string; total: number; under_active_key: number; under_previous_key: number; unrecognised: number }>;
  last_sweep: {
    finishedAt: string;
    durationMs: number;
    totals: { scanned: number; rekeyed: number; alreadyCurrent: number; failed: number };
    operatorEmail: string;
    keyFingerprint: string;
  } | null;
}

interface SweepResult {
  started_at: string;
  finished_at: string;
  duration_ms: number;
  totals: { scanned: number; rekeyed: number; alreadyCurrent: number; failed: number };
  columns: {
    column: string;
    scanned: number;
    already_current: number;
    rekeyed: number;
    failed: number;
    errors: { tenantId: string; message: string }[];
  }[];
}

export function AdminEncryptionPage() {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sweeping, setSweeping] = useState(false);
  const [lastResult, setLastResult] = useState<SweepResult | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<Status>('/v1/admin/encryption/status');
      setStatus(r);
    } catch (err) {
      if (err instanceof ApiError) setError(`Could not load status (HTTP ${err.status}).`);
      else setError('Could not load status.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSweep = useCallback(async () => {
    if (
      !window.confirm(
        'Run encryption sweep? It re-encrypts every _enc column under the active key. ' +
          'Safe to re-run — idempotent. Typically takes <2s for <1000 tenants.',
      )
    )
      return;
    setSweeping(true);
    setError(null);
    setLastResult(null);
    try {
      const r = await api<SweepResult>('/v1/admin/encryption/sweep', { method: 'POST' });
      setLastResult(r);
      await load();
    } catch (err) {
      if (err instanceof ApiError) setError(`Sweep failed (HTTP ${err.status}).`);
      else setError('Sweep failed.');
    } finally {
      setSweeping(false);
    }
  }, [load]);

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Encryption-at-rest</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          AES-256-GCM column-level protection for `_enc` columns
          (`tenants.beacon_secret_enc`, `tenants.s3_secret_key_enc`). Key material lives in Coolify
          env-vars (`OCP_TOKEN_ENCRYPTION_KEY` + optional `_KEYS_PREVIOUS`) — NOT in this UI.
        </p>
      </header>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!status && !error && <p className="text-sm text-gray-500">Loading…</p>}

      {status && (
        <>
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Active key (SHA-256, first 16 hex)" value={status.active_key_fingerprint} mono />
            <Field label="Previous keys in scope" value={String(status.previous_key_count)} />
          </section>

          {status.previous_key_fingerprints.length > 0 && (
            <section className="mt-4">
              <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Previous keys (rotation grace window)</h2>
              <ul className="space-y-1 text-xs font-mono">
                {status.previous_key_fingerprints.map((fp, i) => (
                  <li key={fp} className="rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-2 py-1">
                    [{i + 1}] {fp}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-gray-500">
                These keys decrypt-only (old envelopes still work). Drop them from{' '}
                <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">OCP_TOKEN_ENCRYPTION_KEYS_PREVIOUS</code>{' '}
                only when the table below shows <strong>0 values under a previous key</strong> - every value in every
                encrypted column, not just the tenant secrets.
              </p>
            </section>
          )}

          {status.columns && (
            <section className="mt-4" data-testid="encryption-columns">
              <h2 className="mb-1 text-sm font-semibold text-gray-800 dark:text-gray-200">Encrypted columns</h2>
              <p className={`mb-2 text-xs ${(status.values_under_previous_keys ?? 0) > 0 ? 'text-red-700 dark:text-red-300' : 'text-gray-500'}`} role={(status.values_under_previous_keys ?? 0) > 0 ? 'alert' : undefined}>
                {(status.values_under_previous_keys ?? 0) > 0
                  ? `${status.values_under_previous_keys} value(s) are still encrypted under a previous key. Run the sweep - dropping that key now would make them undecryptable.`
                  : 'Every encrypted value is under the active key.'}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="text-gray-500">
                    <tr><th className="py-1 pr-3">Table</th><th className="pr-3">Column</th><th className="pr-3">Values</th><th className="pr-3">Active key</th><th className="pr-3">Previous key</th><th>Unrecognised</th></tr>
                  </thead>
                  <tbody>
                    {status.columns.map((c) => (
                      <tr key={`${c.table}.${c.column}`} className="border-t border-gray-100 dark:border-gray-800 font-mono">
                        <td className="py-1 pr-3">{c.table}</td>
                        <td className="pr-3">{c.column}</td>
                        <td className="pr-3">{c.total}</td>
                        <td className="pr-3">{c.under_active_key}</td>
                        <td className={`pr-3 ${c.under_previous_key > 0 ? 'text-red-700 dark:text-red-300 font-semibold' : ''}`}>{c.under_previous_key}</td>
                        <td>{c.unrecognised}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="mt-8 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Rekey sweep</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Walks every `_enc` column and re-encrypts anything that isn&apos;t under the active key
              (plain values OR previous-key envelopes). Idempotent.
            </p>

            {status.last_sweep && !lastResult && (
              <div className="mt-4 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3 text-sm">
                <div className="font-medium text-gray-800 dark:text-gray-200">Last sweep</div>
                <div className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                  Finished {fmtDateTime(status.last_sweep.finishedAt)} (
                  {status.last_sweep.durationMs}ms) · by{' '}
                  <span className="font-medium">{status.last_sweep.operatorEmail}</span> · under key{' '}
                  <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">{status.last_sweep.keyFingerprint}</code>
                </div>
                <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                  <Stat label="Scanned" value={status.last_sweep.totals.scanned} />
                  <Stat label="Rekeyed" value={status.last_sweep.totals.rekeyed} highlight={status.last_sweep.totals.rekeyed > 0} />
                  <Stat label="Already current" value={status.last_sweep.totals.alreadyCurrent} />
                  <Stat label="Failed" value={status.last_sweep.totals.failed} highlight={status.last_sweep.totals.failed > 0} danger />
                </div>
              </div>
            )}

            {lastResult && (
              <div className="mt-4 rounded border border-green-300 bg-green-50 p-3 text-sm">
                <div className="font-medium text-green-900">Sweep complete ({lastResult.duration_ms}ms)</div>
                <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                  <Stat label="Scanned" value={lastResult.totals.scanned} />
                  <Stat label="Rekeyed" value={lastResult.totals.rekeyed} highlight={lastResult.totals.rekeyed > 0} />
                  <Stat label="Already current" value={lastResult.totals.alreadyCurrent} />
                  <Stat label="Failed" value={lastResult.totals.failed} highlight={lastResult.totals.failed > 0} danger />
                </div>
                <div className="mt-3 space-y-1 text-xs">
                  {lastResult.columns.map((c) => (
                    <div key={c.column} className="font-mono">
                      <code>{c.column}</code>: scanned {c.scanned}, rekeyed {c.rekeyed}, current{' '}
                      {c.already_current}, failed {c.failed}
                    </div>
                  ))}
                </div>
                {lastResult.columns.some((c) => c.errors.length > 0) && (
                  <div className="mt-3 text-xs">
                    <div className="font-medium text-red-800 dark:text-red-300">Failed rows:</div>
                    {lastResult.columns.flatMap((c) =>
                      c.errors.map((e) => (
                        <div key={`${c.column}-${e.tenantId}`} className="mt-1 font-mono text-red-700 dark:text-red-300">
                          {c.column} · {e.tenantId.slice(0, 8)} · {e.message}
                        </div>
                      )),
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={handleSweep}
              disabled={sweeping}
              className="mt-4 rounded bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sweeping ? 'Sweeping…' : 'Run rekey sweep'}
            </button>
          </section>

          <section className="mt-8 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-4 text-sm">
            <h2 className="font-semibold text-gray-800 dark:text-gray-200">Rotation runbook</h2>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-gray-700 dark:text-gray-300">
              <li>
                Generate a new 64-hex key:{' '}
                <code className="rounded bg-white dark:bg-gray-900 px-1">openssl rand -hex 32</code>
              </li>
              <li>
                In Coolify env-vars: prepend the current key value to{' '}
                <code className="rounded bg-white dark:bg-gray-900 px-1">OCP_TOKEN_ENCRYPTION_KEYS_PREVIOUS</code>
                (comma-separated)
              </li>
              <li>
                Replace <code className="rounded bg-white dark:bg-gray-900 px-1">OCP_TOKEN_ENCRYPTION_KEY</code> with the
                new key
              </li>
              <li>Restart orboto Control — new writes use the new key, old envelopes decrypt via the previous list</li>
              <li>
                Trigger sweep on this page — re-encrypts every row under the new key
              </li>
              <li>
                After a successful sweep with no failed rows, drop the previous key from{' '}
                <code className="rounded bg-white dark:bg-gray-900 px-1">OCP_TOKEN_ENCRYPTION_KEYS_PREVIOUS</code>
              </li>
            </ol>
          </section>
        </>
      )}
    </div>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-sm font-medium text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function Stat({
  label,
  value,
  highlight = false,
  danger = false,
}: {
  label: string;
  value: number;
  highlight?: boolean;
  danger?: boolean;
}) {
  const valueClass = danger
    ? 'text-red-700 dark:text-red-300'
    : highlight
      ? 'text-green-700'
      : 'text-gray-700 dark:text-gray-300';
  return (
    <div className="rounded bg-white dark:bg-gray-900 p-2">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-0.5 text-base font-semibold tabular-nums ${valueClass}`}>{value}</div>
    </div>
  );
}
